import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Check, Minus, Plus, Trash2, X } from 'lucide-react';
import {
  hasPermission,
  LIMITS,
  Permission,
  PERMISSION_INFO,
  SLOWMODE_OPTIONS,
  SPACE_ONLY_PERMISSIONS,
  type ForumLayout,
  type PermissionName,
  type PermissionOverwrite,
} from '@spawnwire/shared';
import { Avatar, Button, clsx, DropdownMenu, Field, Input, Select, Switch, TextArea } from '../../components/ui';
import { http } from '../../lib/api';
import { attempt, loadMembers } from '../../lib/data';
import { navigate } from '../../lib/nav';
import { can, channelPermissions, spacePermissions } from '../../lib/perms';
import { displayNameIn, isMuted, useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { Section, SettingsLayout } from './SettingsLayout';

function slowmodeLabel(s: number): string {
  if (s === 0) return 'Off';
  if (s < 60) return `${s} seconds`;
  if (s < 3600) return `${s / 60} minute${s === 60 ? '' : 's'}`;
  return `${s / 3600} hour${s === 3600 ? '' : 's'}`;
}

const SLOWMODE_TYPES = ['text', 'announcement', 'forum', 'media'];

function Overview({ channelId }: { channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]!);
  const categories = useStore(useShallow((s) => Object.values(s.channels).filter((c) => c.spaceId === channel.spaceId && c.type === 'category')));
  const [name, setName] = useState(channel.name);
  const [topic, setTopic] = useState(channel.topic);
  const [parentId, setParentId] = useState(channel.parentId ?? '');
  const [slowmode, setSlowmode] = useState(String(channel.slowmodeSeconds ?? 0));
  const isForum = channel.type === 'forum' || channel.type === 'media';
  const dirty = name !== channel.name || topic !== channel.topic || parentId !== (channel.parentId ?? '') || Number(slowmode) !== (channel.slowmodeSeconds ?? 0);
  const topicLabel = isForum ? 'Post guidelines' : channel.type === 'stage' ? 'Stage topic' : 'Topic';
  const topicHint = isForum ? 'Shown in the channel header and when someone writes a new post.' : channel.type === 'stage' ? 'What the stage is about, shown to everyone listening.' : 'Shown in the channel header.';
  return (
    <>
      <Section>
        <Field label={channel.type === 'category' ? 'Category name' : 'Channel name'} htmlFor="cs-name">
          <Input id="cs-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
        </Field>
        {channel.type !== 'category' && channel.type !== 'voice' && (
          <Field label={topicLabel} htmlFor="cs-topic" hint={topicHint}>
            <TextArea id="cs-topic" rows={3} value={topic} onChange={(e) => setTopic(e.target.value)} maxLength={1024} />
          </Field>
        )}
        {SLOWMODE_TYPES.includes(channel.type) && (
          <Field label="Slow mode" htmlFor="cs-slow" hint={isForum ? 'How long each member waits between new posts. Moderators are not affected.' : 'How long each member waits between messages, here and in its threads. Moderators are not affected.'}>
            <Select id="cs-slow" value={slowmode} onChange={setSlowmode} options={SLOWMODE_OPTIONS.map((s) => ({ value: String(s), label: slowmodeLabel(s) }))} />
          </Field>
        )}
        {channel.type !== 'category' && (
          <Field label="Category" htmlFor="cs-cat">
            <Select id="cs-cat" value={parentId} onChange={setParentId} options={[{ value: '', label: 'No category' }, ...categories.map((c) => ({ value: c.id, label: c.name }))]} />
          </Field>
        )}
        <Button
          disabled={!dirty || !name.trim()}
          onClick={() =>
            void attempt(
              () =>
                http.patch(`/channels/${channelId}`, {
                  name,
                  topic,
                  ...(channel.type !== 'category' ? { parentId: parentId || null } : {}),
                  ...(SLOWMODE_TYPES.includes(channel.type) ? { slowmodeSeconds: Number(slowmode) } : {}),
                }),
              'Saved.',
            )
          }
        >
          Save changes
        </Button>
      </Section>
      <Section title="Delete" description={channel.type === 'category' ? 'Channels in it move to the top level.' : 'Deletes every message, thread and file in this channel.'}>
        <Button
          variant="danger"
          icon={Trash2}
          onClick={() =>
            ui.confirm({
              title: `Delete ${channel.type === 'category' ? channel.name : `#${channel.name}`}?`,
              body: 'This cannot be undone.',
              confirmLabel: 'Delete',
              danger: true,
              onConfirm: async () => {
                await http.del(`/channels/${channelId}`);
                ui.close();
                navigate(`/app/${channel.spaceId}`);
              },
            })
          }
        >
          Delete {channel.type === 'category' ? 'category' : 'channel'}
        </Button>
      </Section>
    </>
  );
}

/** Forum and media channels: their tags, default layout and whether posts need a tag. */
function ForumSettings({ channelId }: { channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]!);
  const [tags, setTags] = useState<{ id?: string; name: string; emoji: string; moderated: boolean }[]>(() =>
    (channel.availableTags ?? []).map((t) => ({ id: t.id, name: t.name, emoji: t.emoji ?? '', moderated: t.moderated })),
  );
  const [layout, setLayout] = useState<ForumLayout>(channel.defaultLayout ?? 'list');
  const [requireTag, setRequireTag] = useState(!!channel.requireTag);
  const update = (i: number, patch: Partial<(typeof tags)[number]>) => setTags((list) => list.map((t, j) => (j === i ? { ...t, ...patch } : t)));

  return (
    <>
      <Section title="Tags" description={`Labels people add to posts so others can filter them. Up to ${LIMITS.forumTags}. Moderated tags can only be added by people who manage messages.`}>
        <ul className="space-y-2">
          {tags.map((t, i) => (
            <li key={t.id ?? `new-${i}`} className="flex flex-wrap items-center gap-2">
              <Input value={t.emoji} onChange={(e) => update(i, { emoji: e.target.value })} placeholder="🙂" aria-label="Tag emoji" className="w-14 text-center" maxLength={8} />
              <Input value={t.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Tag name" aria-label="Tag name" maxLength={20} className="min-w-40 flex-1" />
              <label className="flex items-center gap-1.5 text-sm text-muted">
                <input type="checkbox" checked={t.moderated} onChange={(e) => update(i, { moderated: e.target.checked })} className="size-4 accent-[var(--p-accent)]" />
                Moderated
              </label>
              <IconButtonLite label={`Remove ${t.name || 'tag'}`} onClick={() => setTags((list) => list.filter((_, j) => j !== i))} />
            </li>
          ))}
        </ul>
        <Button variant="secondary" icon={Plus} disabled={tags.length >= LIMITS.forumTags} onClick={() => setTags((list) => [...list, { name: '', emoji: '', moderated: false }])}>
          Add tag
        </Button>
        <Switch checked={requireTag} onChange={setRequireTag} label="Require a tag on new posts" />
      </Section>
      <Section title="Default layout" description="How posts are shown to people who have not picked a layout themselves.">
        <Select
          value={layout}
          onChange={setLayout}
          options={[
            { value: 'list', label: 'List' },
            { value: 'gallery', label: 'Gallery' },
          ]}
          className="w-48"
        />
      </Section>
      <Button
        disabled={tags.some((t) => !t.name.trim())}
        onClick={() =>
          void attempt(
            () =>
              http.patch(`/channels/${channelId}`, {
                availableTags: tags.map((t) => ({ ...(t.id ? { id: t.id } : {}), name: t.name.trim(), emoji: t.emoji.trim() || null, moderated: t.moderated })),
                defaultLayout: layout,
                requireTag,
              }),
            'Saved.',
          )
        }
      >
        Save changes
      </Button>
    </>
  );
}

function IconButtonLite({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} className="rounded p-1.5 text-muted hover:bg-hover hover:text-danger">
      <X className="size-4" />
    </button>
  );
}

/** Your own notification settings for this channel. */
function ChannelNotifications({ channelId }: { channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]!);
  const setting = useStore((s) => s.notificationSettings[channelId]);
  const muted = useStore((s) => isMuted(s, channelId));
  const put = (body: Record<string, unknown>) => void attempt(() => http.put(`/users/@me/notification-settings/${channelId}`, body));
  return (
    <>
      <Section title="Notify me about" description="Overrides the space's notification setting for this channel only.">
        <Select
          value={setting?.level ?? 'default'}
          onChange={(v) => put({ level: v === 'default' ? null : v })}
          options={[
            { value: 'default', label: 'Use the space setting' },
            { value: 'all', label: 'All messages' },
            { value: 'mentions', label: 'Only @mentions' },
            { value: 'nothing', label: 'Nothing' },
          ]}
          className="w-64"
        />
      </Section>
      <Section title="Mute" description="Muted channels are greyed out, show no unread markers and never notify you.">
        <Switch
          checked={muted}
          onChange={(on) => put({ mutedUntil: on ? new Date(Date.now() + 365 * 86400_000).toISOString() : null })}
          label={`Mute ${channel.type === 'category' ? channel.name : `#${channel.name}`}`}
        />
      </Section>
    </>
  );
}

const CHANNEL_PERMS = (Object.keys(PERMISSION_INFO) as PermissionName[]).filter((p) => !hasPermission(SPACE_ONLY_PERMISSIONS, Permission[p]));

function TriState({ state, onChange, disabled, label }: { state: 'allow' | 'deny' | 'inherit'; onChange: (s: 'allow' | 'deny' | 'inherit') => void; disabled?: boolean; label: string }) {
  const btn = (value: 'allow' | 'deny' | 'inherit', Icon: typeof Check, active: string) => (
    <button
      type="button"
      disabled={disabled}
      aria-label={`${label}: ${value}`}
      aria-pressed={state === value}
      onClick={() => onChange(value)}
      className={clsx('flex size-8 items-center justify-center first:rounded-l-md last:rounded-r-md disabled:opacity-40', state === value ? active : 'text-muted hover:bg-hover')}
    >
      <Icon className="size-4" />
    </button>
  );
  return (
    <div className="flex shrink-0 overflow-hidden rounded-md border border-line">
      {btn('deny', X, 'bg-danger text-white')}
      {btn('inherit', Minus, 'bg-active text-fg')}
      {btn('allow', Check, 'bg-success text-white')}
    </div>
  );
}

function Permissions({ channelId }: { channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]!);
  const space = useStore((s) => s.spaces[channel.spaceId!]!);
  const members = useStore((s) => s.members[channel.spaceId!]);
  const everyone = space.roles.find((r) => r.isDefault)!;
  const [selected, setSelected] = useState<string>(everyone.id);
  const [draft, setDraft] = useState<Record<string, PermissionOverwrite>>({});
  const state = useStore.getState();
  const myChannelPerms = channelPermissions(state, channel);
  const isAdmin = space.ownerId === state.me?.id || hasPermission(spacePermissions(state, space.id), Permission.ADMINISTRATOR);
  const grantable = isAdmin ? 0x7fffffff : myChannelPerms;

  useEffect(() => {
    void loadMembers(space.id).catch(() => {});
  }, [space.id]);

  const overwrites = useMemo(() => {
    const map: Record<string, PermissionOverwrite> = {};
    for (const o of channel.overwrites) map[o.targetId] = o;
    return { ...map, ...draft };
  }, [channel.overwrites, draft]);

  const targets = useMemo(() => {
    const ids = new Set([everyone.id, ...Object.keys(overwrites)]);
    return [...ids].map((id) => {
      const role = space.roles.find((r) => r.id === id);
      return role ? { id, type: 'role' as const, label: role.name, color: role.color } : { id, type: 'member' as const, label: displayNameIn(useStore.getState(), id, space.id), color: null };
    });
  }, [overwrites, space.roles, everyone.id, space.id]);

  const current = overwrites[selected] ?? { targetType: space.roles.some((r) => r.id === selected) ? 'role' : 'member', targetId: selected, allow: 0, deny: 0 };
  const stateOf = (bit: number) => (hasPermission(current.allow, bit) ? 'allow' : hasPermission(current.deny, bit) ? 'deny' : 'inherit');
  const set = (bit: number, value: 'allow' | 'deny' | 'inherit') => {
    const allow = value === 'allow' ? current.allow | bit : current.allow & ~bit;
    const deny = value === 'deny' ? current.deny | bit : current.deny & ~bit;
    setDraft((d) => ({ ...d, [selected]: { ...current, allow, deny } }));
  };
  const original = channel.overwrites.find((o) => o.targetId === selected);
  const dirty = !!draft[selected] && (draft[selected]!.allow !== (original?.allow ?? 0) || draft[selected]!.deny !== (original?.deny ?? 0));

  const isPrivate = hasPermission(overwrites[everyone.id]?.deny ?? 0, Permission.VIEW_CHANNEL);
  const addable = [
    ...space.roles.filter((r) => !overwrites[r.id] && !r.isDefault).map((r) => ({ label: `Role: ${r.name}`, onSelect: () => { setDraft((d) => ({ ...d, [r.id]: { targetType: 'role', targetId: r.id, allow: 0, deny: 0 } })); setSelected(r.id); } })),
    ...Object.values(members ?? {})
      .filter((m) => !overwrites[m.user.id])
      .slice(0, 30)
      .map((m) => ({ label: `Member: ${m.user.displayName}`, onSelect: () => { setDraft((d) => ({ ...d, [m.user.id]: { targetType: 'member', targetId: m.user.id, allow: 0, deny: 0 } })); setSelected(m.user.id); } })),
  ];

  async function save() {
    const o = overwrites[selected]!;
    const ok = await attempt(() => http.put(`/channels/${channelId}/overwrites/${selected}`, { targetType: o.targetType, allow: o.allow, deny: o.deny }), 'Permissions saved.');
    if (ok) setDraft((d) => {
      const next = { ...d };
      delete next[selected];
      return next;
    });
  }

  const parent = channel.parentId ? useStore.getState().channels[channel.parentId] : undefined;
  const key = (list: PermissionOverwrite[]) =>
    list
      .filter((o) => o.allow || o.deny)
      .map((o) => `${o.targetId}:${o.allow}:${o.deny}`)
      .sort()
      .join('|');
  const synced = !!parent && key(channel.overwrites) === key(parent.overwrites);

  return (
    <>
      {parent && channel.type !== 'category' && (
        <Section
          title="Category permissions"
          description={`This channel starts from the permissions of ${parent.name}. Its own overrides below are applied on top: allow and deny here win, and inherit keeps the category's setting.`}
        >
          {synced ? (
            <p className="flex items-center gap-2 text-sm text-success">
              <Check className="size-4" aria-hidden /> Synced with {parent.name}.
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-muted">Not synced: this channel has its own overrides.</p>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  ui.confirm({
                    title: `Sync with ${parent.name}?`,
                    body: "This channel's overrides are replaced with a copy of the category's.",
                    confirmLabel: 'Sync permissions',
                    onConfirm: async () => {
                      await http.post(`/channels/${channelId}/permissions/sync`);
                      setDraft({});
                    },
                  })
                }
              >
                Sync now
              </Button>
            </div>
          )}
        </Section>
      )}
      <Section title={channel.type === 'category' ? 'Private category' : 'Private channel'} description="Only selected roles and members can see it. Unauthorised people can't see its name, messages or files, even through the API.">

        <Switch
          checked={isPrivate}
          onChange={(on) => {
            const o = channel.overwrites.find((x) => x.targetId === everyone.id) ?? { targetType: 'role' as const, targetId: everyone.id, allow: 0, deny: 0 };
            const deny = on ? o.deny | Permission.VIEW_CHANNEL : o.deny & ~Permission.VIEW_CHANNEL;
            const allow = on ? o.allow & ~Permission.VIEW_CHANNEL : o.allow;
            void attempt(() => http.put(`/channels/${channelId}/overwrites/${everyone.id}`, { targetType: 'role', allow, deny }));
          }}
          label="Make this channel private"
        />
      </Section>
      <Section title="Advanced permissions" description="Overrides apply on top of role permissions: @everyone first, then roles, then individual members. Allow wins over deny between roles.">
        <div className="grid gap-5 md:grid-cols-[200px_1fr]">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-faint">Roles and members</span>
              {addable.length > 0 && <DropdownMenu entries={addable} trigger={<button type="button" aria-label="Add a role or member" className="rounded p-1 text-muted hover:bg-hover hover:text-fg"><Plus className="size-4" /></button>} />}
            </div>
            <ul className="space-y-0.5">
              {targets.map((t) => (
                <li key={t.id}>
                  <button type="button" onClick={() => setSelected(t.id)} className={clsx('flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm', selected === t.id ? 'bg-active text-fg' : 'text-muted hover:bg-hover hover:text-fg')}>
                    {t.type === 'member' ? <Avatar user={useStore.getState().users[t.id]} size={18} /> : <span className="size-3 rounded-full" style={{ background: t.color ?? 'var(--p-faint)' }} aria-hidden />}
                    <span className="truncate">{t.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="divide-y divide-line">
              {CHANNEL_PERMS.map((p) => (
                <div key={p} className="flex items-center justify-between gap-4 py-2.5">
                  <div>
                    <div className="text-sm font-medium text-fg">{PERMISSION_INFO[p].label}</div>
                    <div className="text-xs text-muted">{PERMISSION_INFO[p].description}</div>
                  </div>
                  <TriState state={stateOf(Permission[p])} onChange={(v) => set(Permission[p], v)} disabled={!hasPermission(grantable, Permission[p])} label={PERMISSION_INFO[p].label} />
                </div>
              ))}
            </div>
            <div className="sticky bottom-0 flex gap-2 border-t border-line bg-main py-3">
              <Button disabled={!dirty} onClick={() => void save()}>
                Save
              </Button>
              {original && selected !== everyone.id && (
                <Button variant="ghost" onClick={() => void attempt(() => http.del(`/channels/${channelId}/overwrites/${selected}`)).then(() => setSelected(everyone.id))}>
                  Remove override
                </Button>
              )}
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}

export function ChannelSettings({ channelId }: { channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]);
  const perms = useStore((s) => (channel ? channelPermissions(s, channel) : 0));
  if (!channel || !channel.spaceId) return null;
  const manage = can(perms, Permission.MANAGE_CHANNELS);
  const isForum = channel.type === 'forum' || channel.type === 'media';
  return (
    <SettingsLayout
      title={channel.type === 'category' ? channel.name : `#${channel.name}`}
      sections={[
        ...(manage ? [{ id: 'overview', label: 'Overview', render: () => <Overview channelId={channelId} /> }] : []),
        ...(manage && isForum ? [{ id: 'forum', label: channel.type === 'media' ? 'Media settings' : 'Forum settings', render: () => <ForumSettings channelId={channelId} /> }] : []),
        ...(can(perms, Permission.MANAGE_ROLES) ? [{ id: 'permissions', label: 'Permissions', render: () => <Permissions channelId={channelId} /> }] : []),
        { id: 'notifications', label: 'Notifications', render: () => <ChannelNotifications channelId={channelId} /> },
      ]}
    />
  );
}
